import {
  ConditionType,
  type MappingRule,
} from '../pages/CreateCensusTemplate/types'

/**
 * Canonical column names emitted by the partner-census-graph upstream for
 * API integration ingest, filtered to only those that exist on the downstream
 * `person` schema (`personSchemaAttributes` in enrollment-platform-service).
 * Partners may ship additional columns, but the starter API_INTEGRATION
 * mapping only auto-creates passthrough rules for targets the downstream
 * consumer can actually persist. Authors then customize the handful of
 * fields that need transformation (e.g. Salary, IsExempt, EmploymentStatus)
 * on top of this baseline.
 *
 * `SSN` and `Eligibility_1..4` are kept because the backend's
 * `getAllowedCsvFieldsFromSchema()` exposes them on the person schema even
 * though the raw DynamoDB columns differ (`SSNEncrypted` / `SSNHmac` /
 * `SSNLastFour` back `SSN`; `Eligibility1..4` without an underscore back
 * `Eligibility_1..4`).
 *
 * Dropped vs the raw upstream column set:
 *   - `ReHireDate`         — person schema only has `OriginalHireDate`
 *   - `AnnualSalaryAmount` — person schema only has `Salary`
 */
export const PARTNER_API_SOURCE_FIELDS = [
  'CompanyID',
  'CompanyEmployeeId',
  'FirstName',
  'MiddleName',
  'LastName',
  'Gender',
  'Relationship',
  'MarriageStatus',
  'DateOfBirth',
  'Smoker',
  'SSN',
  'StreetAddress',
  'StreetAddressExt',
  'City',
  'StateProvince',
  'PostalCode',
  'County',
  'MailingStreetAddress',
  'MailingStreetAddressExt',
  'MailingCity',
  'MailingStateProvince',
  'MailingPostalCode',
  'EmailAddress',
  'EmailAddressType',
  'PhoneNumber',
  'PhoneType',
  'HireDate',
  'TerminationDate',
  'JobTitle',
  'EmploymentType',
  'EmploymentStatus',
  'PayType',
  'IsExempt',
  'HourlyWage',
  'PayrollCycle',
  'Department',
  'BenefitsEligible',
  'Eligibility_1',
  'Eligibility_2',
  'Eligibility_3',
  'Eligibility_4',
  'CompanyCode',
  'DivisionCode',
  'DepartmentCode',
] as const

export function buildApiIntegrationDefaultRules(): MappingRule[] {
  const passthroughRules = PARTNER_API_SOURCE_FIELDS.map((field, index) => ({
    id: `default-api-integration-${index}-${field}`,
    target: field,
    condition: ConditionType.DIRECT_MAPPING,
    source: field,
    normalize: [],
  }))

  const salaryRule: MappingRule = {
    id: `default-api-integration-${PARTNER_API_SOURCE_FIELDS.length}-Salary`,
    target: 'Salary',
    condition: ConditionType.DIRECT_MAPPING,
    source: 'AnnualSalaryAmount',
    normalize: [],
  }

  return [...passthroughRules, salaryRule]
}
